import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import isEqual from "lodash.isequal";
import * as React from "react";
import {useEffect, useRef, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {BodyText} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";

import {SaveChangesButton} from "../save-changes-button.tsx";
import type {AccessibilitySettingsData} from "../types/account-settings-types.ts";

import AccessibilitySettingsForm from "./accessibility-settings-form.tsx";
import SET_ACCESSIBILITY_SETTINGS_MUTATION from "./set-accessibility-settings-mutation.ts";

interface Props {
    initialSettings: AccessibilitySettingsData;
    kaid: string;
    successfullyChangedSettings: () => unknown;
}

export const AccessibilitySettings: React.FC<Props> = ({
    initialSettings,
    kaid,
    successfullyChangedSettings,
}) => {
    const [currentSettings, setCurrentSettings] =
        useState<AccessibilitySettingsData>(initialSettings);
    const [isUnexpectedError, setIsUnexpectedError] = useState(false);
    const isMounted = useRef(true);

    const [setAccessibilityMutation, result] = useMutation(
        SET_ACCESSIBILITY_SETTINGS_MUTATION,
    );

    const isSavingSettings = result?.status === "loading";

    const isSaveChangesDisabled =
        isSavingSettings || isEqual(initialSettings, currentSettings);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleSettingsChange = (
        newSettings: Partial<AccessibilitySettingsData>,
    ) => {
        setIsUnexpectedError(false);
        setCurrentSettings((prevSettings) => ({
            ...prevSettings,
            ...newSettings,
        }));
    };

    const setSettings = async (settings: AccessibilitySettingsData) => {
        const {hideVisual, prefersReducedMotion, noColorInVideos, soundOn} =
            settings;

        setIsUnexpectedError(false);

        try {
            const result = await setAccessibilityMutation({
                kaid,
                hideVisual,
                prefersReducedMotion,
                noColorInVideos,
                soundOn,
            });

            if (!isMounted.current) {
                return;
            }

            if (result.status === "success") {
                const errors = result.data?.setSettings?.errors;
                if (errors && errors.length > 0) {
                    throw new KAError(errors[0]?.code ?? "", Errors.Internal);
                }

                successfullyChangedSettings();
            } else if (result.status === "error") {
                setIsUnexpectedError(true);
            }
        } catch (error) {
            if (!isMounted.current) {
                return;
            }

            setIsUnexpectedError(true);
        }
    };

    const {hideVisual, prefersReducedMotion, noColorInVideos, soundOn} =
        currentSettings;

    // NOTE: This should never be rendered on the server during SSR.
    // Settings are for logged in users and logged in pages are not SSR'd.
    // If that changes, it's likely that we switched to a non-window-based
    // SSR process, in which case this code needs updating as it will cause
    // a hydration error if the result is true on the client but false
    // on the server.
    const systemReduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion)").matches;

    return (
        <View
            testId="accessibility-settings"
            role="region"
            aria-label={t`Accessibility settings`}
        >
            <AccessibilitySettingsForm
                hideVisual={hideVisual}
                prefersReducedMotion={prefersReducedMotion}
                noColorInVideos={noColorInVideos}
                soundOn={soundOn}
                onSettingsChange={handleSettingsChange}
                disabled={false}
                footer={
                    <>
                        {systemReduceMotion && (
                            <BodyText style={styles.reduceMotionText}>
                                {t`Your system settings indicate that you prefer reduced motion, so in this browser, animations will be reduced regardless of this setting.`}
                            </BodyText>
                        )}
                        <SaveChangesButton
                            setSettings={() => setSettings(currentSettings)}
                            isLoading={isSavingSettings}
                            isDisabled={isSaveChangesDisabled}
                            hasUnexpectedError={isUnexpectedError}
                            testId="save-a11y-settings-btn"
                        />
                    </>
                }
            />
        </View>
    );
};

const styles = StyleSheet.create({
    reduceMotionText: {
        paddingBlockStart: sizing.size_120,
    },
});

export default AccessibilitySettings;
